import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { FocusTrap } from "focus-trap-react";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import NavItem from "./NavItem";
import { NavSectionConfig } from "../../../config/navigation";
import { SidebarMode } from "../../../hooks/useSidebar";
import { AppRoutes } from "../../../utils";

interface SidebarProps {
  mode: SidebarMode;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onToggleCollapsed: () => void;
  sections: NavSectionConfig[];
  pathname: string;
}

function NavList({
  sections,
  pathname,
  collapsed,
}: {
  sections: NavSectionConfig[];
  pathname: string;
  collapsed: boolean;
}) {
  return (
    <nav className="flex flex-col grow overflow-y-auto px-3" aria-label="Main">
      {sections.map((group) => (
        <div key={group.section} className="mb-4">
          {group.label &&
            (collapsed ? (
              <div className="mx-2 mb-2 border-t border-gray-200" />
            ) : (
              <p className="px-4 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {group.label}
              </p>
            ))}
          <ul>
            {group.items.map((item) => (
              <NavItem
                key={item.slug}
                item={item}
                isActive={pathname.startsWith(item.path)}
                collapsed={collapsed}
              />
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function Logo({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      className={`flex items-center h-16 mb-4 ${
        collapsed ? "justify-center px-2" : "px-4"
      }`}
    >
      <Link to={AppRoutes.DASHBOARD} aria-label="Go to dashboard">
        {collapsed ? (
          <img src="/images/logo.svg" alt="Passportmonie" className="h-8 w-8" />
        ) : (
          <img
            src="/images/logo.jpeg"
            alt="Passportmonie"
            className="h-10 w-auto"
          />
        )}
      </Link>
    </div>
  );
}

export default function Sidebar({
  mode,
  mobileOpen,
  onCloseMobile,
  onToggleCollapsed,
  sections,
  pathname,
}: SidebarProps) {
  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (!(mode === "drawer" && mobileOpen)) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mode, mobileOpen]);

  // Escape closes the mobile drawer
  useEffect(() => {
    if (!(mode === "drawer" && mobileOpen)) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseMobile();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mode, mobileOpen, onCloseMobile]);

  if (mode === "drawer") {
    return (
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={onCloseMobile}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-hidden="true"
            />
            <FocusTrap
              focusTrapOptions={{
                clickOutsideDeactivates: true,
                escapeDeactivates: false,
              }}
            >
              <motion.aside
                className="fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-white z-50 flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "tween", ease: "easeInOut" }}
              >
                <div className="flex items-center justify-between pr-3">
                  <Logo collapsed={false} />
                  <button
                    onClick={onCloseMobile}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-md"
                    aria-label="Close navigation menu"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <NavList
                  sections={sections}
                  pathname={pathname}
                  collapsed={false}
                />
              </motion.aside>
            </FocusTrap>
          </>
        )}
      </AnimatePresence>
    );
  }

  const collapsed = mode === "rail";

  return (
    <aside
      className={`fixed inset-y-0 left-0 bg-white z-30 flex flex-col border-r border-gray-100 transition-[width] duration-300 ${
        collapsed ? "w-16" : "w-64"
      }`}
    >
      <Logo collapsed={collapsed} />
      <NavList sections={sections} pathname={pathname} collapsed={collapsed} />
      <div className="border-t border-gray-100 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <button
          onClick={onToggleCollapsed}
          className={`flex items-center rounded-md min-h-[44px] text-gray-500 hover:bg-primary-100 transition-colors w-full ${
            collapsed ? "justify-center" : "pl-4"
          }`}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <>
              <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
              <span className="ml-3 text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
